import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Checkout from './pages/Checkout';
import ManageBilling from './pages/ManageBilling';
import Survey from './pages/Survey';
import Verify from './pages/Verify';
import Blog, { BlogArticle } from './pages/Blog';
import RecipePage from './pages/Recipe';
import HowItWorks from './pages/HowItWorks';
import Settings from './pages/Settings';
import Feedback from './pages/Feedback';
import Welcome from './pages/Welcome';
import FocusAreas from './pages/FocusAreas';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import WorkoutRoutine from './pages/WorkoutRoutine';
import SurveyDemo from './pages/SurveyDemo';
import WorkoutBuilderDemo from './pages/WorkoutBuilderDemo';
import WorkoutBuilder from './pages/WorkoutBuilder';
import YogaDemo from './pages/YogaDemo';
import ExerciseVideoDemo from './pages/ExerciseVideoDemo';
import EditPlanDemo from './pages/EditPlanDemo';
import PlanOverview from './pages/PlanOverview';
import EditPlan from './pages/EditPlan';
import PuzzlesPage from './pages/Puzzles';
import NutritionSummaryPage from './pages/NutritionSummary';
import WeeklySummaryPage from './pages/WeeklySummary';
import CoachingBriefPage from './pages/CoachingBrief';
import LocalEventsPage from './pages/LocalEvents';
import FoodScanPage from './pages/FoodScan';
import IngredientDetailPage from './pages/IngredientDetail';
import Guide from './pages/Guide';
import WellnessPhilosophy from './pages/WellnessPhilosophy';
import Features from './pages/Features';
import FAQ from './pages/FAQ';
import MealPlannerPage from './pages/MealPlannerPage';
import GroceryListPage from './pages/GroceryListPage';
import DailyLogPage from './pages/DailyLogPage';
import DailyLogDemoPage from './pages/DailyLogDemo';

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-logo-wrapper">
        <img src="/mosaic-icon.png" alt="mosaime" className="loading-logo" />
        <div className="loading-ring" />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function SurveyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If user has completed survey, redirect to settings (home for logged-in users)
  if (user?.surveyCompleted) {
    return <Navigate to="/settings" replace />;
  }
  
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/settings" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/features" element={<Features />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/philosophy" element={<WellnessPhilosophy />} />
      <Route path="/wellness-philosophy" element={<Navigate to="/philosophy" replace />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/billing" element={
        <ProtectedRoute>
          <Layout>
            <ManageBilling />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/verify" element={<PublicRoute><Verify /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      
      {/* Survey page - for new users */}
      <Route path="/survey" element={
        <SurveyRoute>
          <Survey />
        </SurveyRoute>
      } />


      {/* Focus areas intro - shown after survey, before welcome */}
      <Route path="/focus-areas" element={
        <ProtectedRoute>
          <FocusAreas />
        </ProtectedRoute>
      } />

      {/* Welcome page - shown after survey completion */}
      <Route path="/welcome" element={
        <ProtectedRoute>
          <Welcome />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes with Layout */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <Layout>
            <Feedback />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Survey demo — public, no auth, no data saved */}
      <Route path="/survey-demo" element={<SurveyDemo />} />

      {/* Workout plan builder — public, userId in query param ?u=<id> */}
      <Route path="/plan/create" element={<WorkoutBuilder />} />

      {/* Workout builder demo — public, no auth, no data saved */}
      <Route path="/workout-builder-demo" element={<WorkoutBuilderDemo />} />

      {/* Edit plan demo — public, no auth, no data saved */}
      <Route path="/plan-demo/edit" element={<EditPlanDemo />} />

      {/* Yoga image mockup — public, no auth, no data */}
      <Route path="/yoga-demo" element={<YogaDemo />} />

      {/* Exercise video layout playground — public, no auth, no data */}
      <Route path="/video-demo" element={<ExerciseVideoDemo />} />

      {/* Workout prototype — public, no auth */}
      <Route path="/workout/:nanoid" element={<WorkoutRoutine />} />

      {/* Plan overview — public, no auth */}
      <Route path="/plan/:nanoid" element={<PlanOverview />} />

      {/* Plan edit — public, nanoid is the access token */}
      <Route path="/plan/:nanoid/edit" element={<EditPlan />} />

      {/* Daily puzzles — public, no auth */}
      <Route path="/puzzles/:nanoid" element={<PuzzlesPage />} />

      {/* Public blog */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />

      {/* Public recipe pages — no login required, not in nav */}
      <Route path="/recipes/:recipe_id" element={<RecipePage />} />

      {/* Public nutrition summary pages — no login required */}
      <Route path="/nutrition/:nanoid" element={<NutritionSummaryPage />} />

      {/* Public weekly summary pages — no login required */}
      <Route path="/weekly/:nanoid" element={<WeeklySummaryPage />} />

      {/* Public coaching brief pages — no login required */}
      <Route path="/coaching/:nanoid" element={<CoachingBriefPage />} />

      {/* Public local events pages — no login required */}
      <Route path="/local/:nanoid" element={<LocalEventsPage />} />

      {/* Public food scan / ingredient deconstructor — no login required */}
      <Route path="/foodscan/:scan_id" element={<FoodScanPage />} />
      <Route path="/ingredients/:slug" element={<IngredientDetailPage />} />

      {/* Meal planner demo — public, no auth, no data saved */}
      <Route path="/meal-plan/demo" element={<MealPlannerPage />} />
      {/* Meal planner intake form — public, nanoid as access token, expires 7 days */}
      <Route path="/meal-plan/:nanoid" element={<MealPlannerPage />} />

      {/* Grocery list — public, no expiry */}
      <Route path="/grocery/:nanoid" element={<GroceryListPage />} />
      {/* Daily log demo — public, hardcoded mock data, no API calls */}
      <Route path="/log/demo" element={<DailyLogDemoPage />} />
      {/* Daily wellness log — public, sent via dinner_checkin SMS at 7:30 PM */}
      <Route path="/log/:nanoid" element={<DailyLogPage />} />
      
      {/* Legacy redirects */}
      <Route path="/chat" element={<Navigate to="/settings" replace />} />
      <Route path="/dashboard" element={<Navigate to="/settings" replace />} />
      <Route path="/mind" element={<Navigate to="/settings" replace />} />
      <Route path="/diet" element={<Navigate to="/settings" replace />} />
      <Route path="/exercise" element={<Navigate to="/settings" replace />} />
      <Route path="/life" element={<Navigate to="/settings" replace />} />
      <Route path="/food" element={<Navigate to="/settings" replace />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
